import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Modal, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import StarRating from '../StarRating/StarRating';
import { DESIGN_TOKENS } from '../../../styles/constants';

interface ReplyModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reply: string) => void;
  reviewData: {
    userName: string;
    date: string;
    rating: number;
    reviewText: string;
  };
  existingReply?: string;
}

const ReplyModal: React.FC<ReplyModalProps> = ({
  open,
  onClose,
  onSubmit,
  reviewData,
  existingReply = '',
}) => {
  const [reply, setReply] = useState(existingReply);

  const handleSubmit = () => {
    onSubmit(reply);
    setReply('');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '80px 24px 40px',
      }}
    >
      <Box
        sx={{
          width: '508px',
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          boxShadow: '0px 80px 250px 0px rgba(26, 109, 109, 0.1)',
          outline: 'none',
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            padding: '24px',
            borderBottom: '1px solid #F1F4F4',
          }}
        >
          <Typography
            sx={{
              fontFamily: DESIGN_TOKENS.typography.fontFamily,
              fontSize: '22px',
              fontWeight: 700,
              lineHeight: '26.4px',
              letterSpacing: '-0.11px',
              color: '#181818',
            }}
          >
            Reply
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              width: '40px',
              height: '40px',
              backgroundColor: '#F6F8F8',
              borderRadius: '40px',
              '&:hover': {
                backgroundColor: '#E5E8E8',
              },
            }}
          >
            <Close sx={{ fontSize: 24, color: '#181818' }} />
          </IconButton>
        </Box>

        {/* Modal Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            padding: '32px 24px 24px',
          }}
        >
          {/* Review Info */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Box sx={{ display: 'flex', gap: '4px' }}>
              <Typography
                sx={{
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  fontSize: '13px',
                  fontWeight: 400,
                  lineHeight: '18.2px',
                  letterSpacing: '-0.08px',
                  color: '#181818',
                }}
              >
                {reviewData.userName} on
              </Typography>
              <Typography
                sx={{
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  fontSize: '13px',
                  fontWeight: 400,
                  lineHeight: '18.2px',
                  letterSpacing: '-0.08px',
                  color: '#181818',
                }}
              >
                {reviewData.date}
              </Typography>
            </Box>
            
            <StarRating rating={reviewData.rating} showNumber={true} size="small" />
            
            <Typography
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '22.4px',
                color: '#181818',
              }}
            >
              {reviewData.reviewText}
            </Typography>
          </Box>

          {/* Reply Input */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '4px',
            }}
          >
            <TextField
              multiline
              rows={4}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write your reply..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F6F8F8',
                  borderRadius: '12px',
                  padding: '6px 12px',
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  fontSize: '16px',
                  lineHeight: '22.4px',
                  color: '#181818',
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: 0,
                },
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    sx={{
                      fontFamily: DESIGN_TOKENS.typography.fontFamily,
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '22.4px',
                      color: '#93989C',
                      marginBottom: '4px',
                    }}
                  >
                    Your reply
                  </Typography>
                ),
              }}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Button
              onClick={handleSubmit}
              disabled={!reply.trim()}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2px 32px 0px',
                height: '56px',
                backgroundColor: reply.trim() ? '#67314F' : '#F1F4F4',
                borderRadius: '16px',
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '24px',
                letterSpacing: '-0.32px',
                color: reply.trim() ? '#FFFFFF' : '#C5CBD3',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: reply.trim() ? '#5a2a42' : '#F1F4F4',
                },
              }}
            >
              Submit
            </Button>
            
            <Button
              onClick={onClose}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2px 32px 0px',
                height: '56px',
                borderRadius: '16px',
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '24px',
                letterSpacing: '-0.32px',
                color: '#67314F',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(103, 49, 79, 0.04)',
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReplyModal;


